/**
 * Every named test of a run (contract v3 "Tests and coverage drill-down"): per service, searchable, filtered by status
 * and service, sorted by file or slowest first, grouped by file with counts. A failed (or flaky) test opens to its
 * message, the diff (expected / received or the runner's diff), the stack with each frame inside the service opening
 * the Code tab at that line, the test's own stdout / stderr, and "Fix with AI" with the test's error.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretRight, Sparkle } from '@phosphor-icons/react';
import { Pill } from './QualityCommon';
import { TEST_STATUS, codePath, formatDuration, plural, testCasePrompt, testStatusOf } from './qualityModel';

function DiffBlock({ failure }) {
  let lines = [];
  if (failure.diff) {
    lines = String(failure.diff).split('\n');
  } else if (failure.expected !== undefined || failure.received !== undefined) {
    lines = [`- Expected  ${String(failure.expected ?? '')}`, `+ Received  ${String(failure.received ?? '')}`];
  }
  if (!lines.length) { return null; }
  return (
    <pre aria-label="Difference" className="quality-err">
      {lines.map((line, i) => {
        const tone = line.startsWith('-') ? 'is-good' : line.startsWith('+') ? 'is-bad' : 'is-dim';
        return <React.Fragment key={i}>{i > 0 && '\n'}<span className={tone}>{line}</span></React.Fragment>;
      })}
    </pre>
  );
}

DiffBlock.propTypes = { failure: PropTypes.object.isRequired };

function TestDetail({ test, canEdit, onAskAI, onOpenCode }) {
  const failure = test.failure || null;
  const frames = failure?.frames || [];
  const stack = failure?.stack ? String(failure.stack).split('\n').filter((l) => l.trim() && l.trim() !== failure.message).slice(0, 12) : [];
  return (
    <div className="quality-test-detail" data-testid={`quality-test-detail-${test.id}`}>
      {failure?.message && <pre className="quality-err"><span className="is-bad">{failure.message}</span></pre>}
      {failure && <DiffBlock failure={failure} />}
      {frames.length > 0 && (
        <div className="quality-small">
          <span className="quality-muted">Stack: </span>
          <ul aria-label="Stack frames" className="quality-test-frames">
            {frames.map((f, i) => {
              const label = `${f.file}:${f.line}${f.column ? `:${f.column}` : ''}`;
              return (
                <li key={`${label}-${i}`}>
                  {onOpenCode
                    ? <button className="quality-link quality-mono" onClick={() => onOpenCode({ path: codePath(test.service, f.file), line: f.line })} type="button">{label}</button>
                    : <span className="quality-mono">{label}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {stack.length > 0 && <pre aria-label="Stack" className="quality-err">{stack.map((line, i) => <React.Fragment key={i}>{i > 0 && '\n'}<span className="is-dim">{line}</span></React.Fragment>)}</pre>}
      {test.stdout && <><span className="quality-cap">stdout</span><pre aria-label="stdout" className="quality-pre quality-mono">{test.stdout}</pre></>}
      {test.stderr && <><span className="quality-cap">stderr</span><pre aria-label="stderr" className="quality-pre quality-mono">{test.stderr}</pre></>}
      {!failure && !test.stdout && !test.stderr && <p className="quality-muted quality-small">Nothing more for this test.</p>}
      <div className="quality-actions">
        {failure && canEdit && onAskAI && <button className="quality-btn is-sm is-primary" onClick={() => onAskAI(testCasePrompt(test))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>}
        {test.file && onOpenCode && <button className="quality-btn is-sm" onClick={() => onOpenCode({ path: codePath(test.service, test.file), line: test.line || undefined })} type="button">Open in Code</button>}
      </div>
    </div>
  );
}

TestDetail.propTypes = { canEdit: PropTypes.bool, onAskAI: PropTypes.func, onOpenCode: PropTypes.func, test: PropTypes.object.isRequired };

const matches = (test, words) => !words || [test.service, test.file, test.suite, test.name, test.failure?.message].filter(Boolean).join(' ').toLowerCase().includes(words);

export default function QualityTestTable({ services = [], canEdit = false, onAskAI = null, onOpenCode = null, truncated = false }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [service, setService] = useState('');
  const [sort, setSort] = useState('file');
  const [open, setOpen] = useState(() => new Set());
  const all = useMemo(() => services.flatMap((s) => (s.tests || []).map((t) => ({ ...t, service: t.service || s.service }))), [services]);
  const names = services.map((s) => s.service);
  const words = q.trim().toLowerCase();
  const shown = all.filter((t) => (!status || t.status === status) && (!service || t.service === service) && matches(t, words));
  const counts = Object.fromEntries(Object.keys(TEST_STATUS).map((id) => [id, all.filter((t) => t.status === id).length]));

  // Grouped by file (service and file), each group with its counts; slowest first sorts the tests and the groups.
  const groups = [];
  const byKey = new Map();
  shown.forEach((t) => {
    const key = `${t.service}\u0000${t.file || ''}`;
    if (!byKey.has(key)) { const g = { key, service: t.service, file: t.file, tests: [] }; byKey.set(key, g); groups.push(g); }
    byKey.get(key).tests.push(t);
  });
  const slowest = (a, b) => (b.durationMs || 0) - (a.durationMs || 0);
  if (sort === 'slowest') {
    groups.forEach((g) => g.tests.sort(slowest));
    groups.sort((a, b) => slowest(a.tests[0], b.tests[0]));
  } else {
    groups.sort((a, b) => `${a.service}/${a.file || ''}`.localeCompare(`${b.service}/${b.file || ''}`));
  }
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });

  if (!all.length) { return <p className="quality-muted quality-small" data-testid="quality-test-table-empty">No named test in this run.</p>; }

  return (
    <div className="quality-stack" data-testid="quality-test-table">
      <div className="quality-filters">
        <input aria-label="Search tests" className="quality-input" onChange={(e) => setQ(e.target.value)} placeholder="Search a test, a file, an error" type="search" value={q} />
        <select aria-label="Status" className="quality-input" onChange={(e) => setStatus(e.target.value)} value={status}>
          <option value="">Every status ({all.length})</option>
          {Object.entries(TEST_STATUS).map(([id, s]) => <option key={id} value={id}>{s.label} ({counts[id]})</option>)}
        </select>
        {names.length > 1 && (
          <select aria-label="Service" className="quality-input" onChange={(e) => setService(e.target.value)} value={service}>
            <option value="">Every service</option>
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        <select aria-label="Sort" className="quality-input" onChange={(e) => setSort(e.target.value)} value={sort}>
          <option value="file">By file</option>
          <option value="slowest">Slowest first</option>
        </select>
      </div>
      <span className="quality-muted quality-small" role="status">{plural(shown.length, 'test')} of {all.length}{truncated ? ' (the run kept the first ones only)' : ''}</span>
      {groups.map((g) => {
        const failed = g.tests.filter((t) => t.status === 'failed').length;
        return (
          <section aria-label={`${g.service} ${g.file || 'no file'}`} className="quality-test-group" data-testid="quality-test-group" key={g.key}>
            <div className="quality-test-group-head">
              <span className="quality-mono quality-small">{g.service}{g.file ? ` › ${g.file}` : ''}</span>
              <span className="quality-muted quality-small">{plural(g.tests.length, 'test')}{failed ? `, ${failed} failed` : ''}</span>
            </div>
            <ul className="quality-test-rows">
              {g.tests.map((t) => {
                const st = testStatusOf(t.status);
                const expandable = Boolean(t.failure || t.stdout || t.stderr);
                const isOpen = open.has(t.id);
                return (
                  <li className={`is-${t.status}`} data-testid={`quality-test-${t.id}`} key={t.id}>
                    <div className="quality-prov-line">
                      {expandable
                        ? <button aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} ${t.name}`} className="quality-btn is-sm is-quiet" onClick={() => toggle(t.id)} type="button">{isOpen ? <CaretDown aria-hidden size={12} /> : <CaretRight aria-hidden size={12} />}</button>
                        : <span aria-hidden className="quality-test-spacer" />}
                      <Pill className="is-xs" tone={st.tone}>{st.label}</Pill>
                      <span className="quality-grow quality-test-name">{t.suite ? <span className="quality-muted">{t.suite} › </span> : null}{t.name}</span>
                      {t.retries > 0 && <span className="quality-muted quality-small">{plural(t.retries, 'retry', 'retries')}</span>}
                      <span className="quality-muted quality-small quality-test-time">{t.durationMs === null || t.durationMs === undefined ? '–' : formatDuration(t.durationMs)}</span>
                    </div>
                    {isOpen && <TestDetail canEdit={canEdit} onAskAI={onAskAI} onOpenCode={onOpenCode} test={t} />}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {!shown.length && <p className="quality-muted">No test matches.</p>}
    </div>
  );
}

QualityTestTable.propTypes = {
  canEdit: PropTypes.bool,
  onAskAI: PropTypes.func,
  // ({ path, line }) => void: the Code tab at that line.
  onOpenCode: PropTypes.func,
  // `[{ service, tests: TestCase[] }]` (RunDetail.tests.services).
  services: PropTypes.array,
  truncated: PropTypes.bool,
};
